import { isEqual } from 'lodash';
import React from 'react';
import DateField from '../../../components/forms/DateField';
import useProviderTypes from '../../../hooks/api-calls/useProviderTypes';
import CustomSelect from '../../../components/forms/CustomSelect';
import { providerStatuses, retributionTypes } from '../../../utils/SelectorMapping';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useCompanies from '../../../hooks/api-calls/useCompanies';
import CustomTextDelayInput from '../../../components/forms/CustomTextDelayInput';

interface ProvidersFiltersProps {
    updateFilters: (filters: any) => void;
    filters: any;
}

const ProvidersFilters: React.FC<ProvidersFiltersProps> = ({ filters, updateFilters }) => {

    // HOOKS

    const { userCan } = usePrivilege();
    const { getProviderTypesList } = useProviderTypes(filters.filter_filters?.company ?? null);
    const { getCompaniesList } = useCompanies();

    // RENDER 

    return (
        <>
            <CustomTextDelayInput
                id="companyName"
                label="Razón social"
                placeholder="Buscar por razón social"
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomTextDelayInput
                id="commercial_name"
                label="Nombre comercial"
                placeholder="Buscar por nombre comercial"
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomTextDelayInput
                id="cif"
                label="CIF"
                placeholder="Buscar por CIF"
                filters={filters}
                updateFilters={updateFilters}
            />


            <CustomSelect
                required
                isSearchable
                id={'provider_type'}
                label="Tipo de proveedor"
                value={{ value: filters.filter_filters?.provider_type, label: getProviderTypesList()?.find((g: any) => g.value === filters.filter_filters?.provider_type)?.label }}
                options={getProviderTypesList()}
                onChange={
                    (e: any) => {
                        updateFilters({ provider_type: e?.value });
                    }
                }
            />

            <CustomSelect
                required
                isSearchable
                id={'retribution_type'}
                label="Tipo de retribución"
                value={{ value: filters.filter_filters?.retribution_type, label: retributionTypes.find((g: any) => g.value === filters.filter_filters?.retribution_type)?.label }}
                options={retributionTypes}
                onChange={
                    (e: any) => { updateFilters({ retribution_type: e?.value }) }
                }
            />

            <CustomSelect
                isSearchable
                id={'status'}
                label="Estado"
                options={[
                    { label: 'Todos', value: 'null' },
                    ...providerStatuses
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ status: null });
                        return;
                    }
                    updateFilters({ status: e.value });
                }}
                value={filters.filter_filters?.status !== null && filters.filter_filters?.status !== undefined
                    ? { label: providerStatuses.find(s => s.value === filters.filter_filters?.status)?.label, value: filters.filter_filters?.status }
                    : { label: 'Todos', value: 'null' }}
            />

            <CustomSelect
                isSearchable
                id={'auto_invoice'}
                label="Auto Factura"
                options={[
                    { label: 'Todos', value: 'null' },
                    { label: 'Sí', value: true },
                    { label: 'No', value: false }
                ]}
                onChange={(e: any) => {
                    if (e.value === 'null') {
                        updateFilters({ auto_invoice: null });
                        return;
                    }
                    updateFilters({ auto_invoice: e.value });
                }}
                value={filters.filter_filters?.auto_invoice !== null && filters.filter_filters?.auto_invoice !== undefined
                    ? { label: filters.filter_filters?.auto_invoice ? 'Sí' : 'No', value: filters.filter_filters?.auto_invoice }
                    : { label: 'Todos', value: 'null' }}
            />

            <DateField
                isClearable
                className="w-full"
                label="Fecha de Creación"
                placeholderText="Selecciona las fechas"
                selectedDateField={filters.filter_filters?.between_dates?.type || "createdAt"}
                dateTypesOptions={[
                    { label: "Fecha de creación", value: "createdAt" },
                    { label: "Fecha de modificación", value: "updatedAt" }
                ]}
                startDate={filters.filter_filters?.between_dates?.startDate ? new Date(filters.filter_filters.between_dates.startDate) : null}
                endDate={filters.filter_filters?.between_dates?.endDate ? new Date(filters.filter_filters.between_dates.endDate) : null}
                onChange={(date: any) => {
                    const filters_between_dates = filters.filter_filters?.between_dates;
                    const between_dates = date ? { startDate: date[0], endDate: date[1] } : null;

                    if (between_dates && !isEqual(filters_between_dates, between_dates)) {
                        updateFilters({ between_dates: { startDate: date[0], endDate: date[1], type: date[2] } });
                    } else if (!between_dates && filters_between_dates) {
                        updateFilters({ between_dates: null });
                    }
                }}
            />

            {userCan('list', 'company') && (
                <CustomSelect
                    isSearchable
                    id="company"
                    label="Compañía"
                    options={getCompaniesList()}
                    value={getCompaniesList().find((item: any) => item.value === filters.filter_filters?.company) ?? { label: 'Todas', value: 'null' }}
                    onChange={(e: any) => {
                        if (e.value === 'null') {
                            updateFilters({ company: null });
                            return;
                        }
                        updateFilters({ company: e.value });
                    }}
                />
            )}

            <CustomTextDelayInput
                id="code"
                label="Código"
                placeholder="Buscar por código"
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomTextDelayInput
                id="email"
                label="Email"
                placeholder="Buscar por email"
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomTextDelayInput
                id="telephone"
                label="Teléfono"
                placeholder="Buscar por teléfono"
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomTextDelayInput
                id="activity"
                label="Actividad"
                placeholder="Buscar por actividad"
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomTextDelayInput
                id="website"
                label="Sitio web"
                placeholder="Buscar por sitio web"
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomTextDelayInput
                id="tax_domicile"
                label="Domicilio fiscal"
                placeholder="Buscar por domicilio fiscal"
                filters={filters}
                updateFilters={updateFilters}
            />
        </>
    );
};

export default ProvidersFilters;